import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';

import { useEntitlements } from '../../../../hooks/useEntitlements';
import { useGetReceivers } from '../../../../queries/receivers';
import { useGetReceiverChannels } from '../../../../queries/channels';

const asList = (d) => (Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : []);

/**
 * Step: pick the match SOURCE — a receiver (beIN box / TV tuner) and, if it has
 * channels configured, which channel to tune. No hardware → a friendly "ask your
 * installer" panel instead of a dead-end.
 *
 * ⚠️ MARKUP/CSS PORTED FROM THE PROTOTYPE's stepSource() — "كما هي اليوم"
 * (as it is today): the LOGIC here is exactly what it was before this pass
 * (receiver → channel → the same explainer + silent-case copy fixed two
 * rounds ago). Only the class names changed, to fc-row/fc-panel/fc-inote
 * instead of the old Bootstrap-ish ad hoc classes, so this step reads as
 * part of the same screen as Content/Save now.
 *
 * Props: receiverId, onReceiver(id), channelId, onChannel(id|null)
 */
export default function SourcePickStep({ receiverId, onReceiver, channelId, onChannel }) {
  const { t } = useTranslation();
  const hwOn = useEntitlements().has('hardware_control');
  const rq = useGetReceivers({}, { enabled: hwOn });
  const cq = useGetReceiverChannels(receiverId, { enabled: hwOn });

  const receivers = asList(rq.data);
  const channels = asList(cq.data);

  const pickReceiver = (id) => { onReceiver(id); onChannel(null); };

  return (
    <div>
      <div className="fc-p-t">{t('cafe_wizard.match_source')}</div>
      <div className="fc-p-s">{t('cafe_wizard.match_source_explainer')}</div>

      {rq.isLoading ? (
        <div className="d-flex flex-column" style={{ gap: 9 }}>
          <Skeleton height="58px" borderRadius="11px" />
          <Skeleton height="58px" borderRadius="11px" />
        </div>
      ) : receivers.length === 0 ? (
        <div className="fc-inote">
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('cafe_wizard.no_sources_title')}</div>
          {t('cafe_wizard.no_sources_hint')}
        </div>
      ) : (
        receivers.map((r) => {
          const on = receiverId === r.id;
          const connected = !!(r.player || r.connected_player_id);
          return (
            <button
              key={r.id} type="button" className="fc-row"
              aria-pressed={on}
              onClick={() => pickReceiver(r.id)}
            >
              <span className="ic">📡</span>
              <span className="b">
                <span className="t">{r.name || t('cafe_wizard.match_source')}</span><br />
                {!connected && <span className="s" style={{ color: '#9A1414' }}>{t('cafe_wizard.source_not_connected')}</span>}
              </span>
            </button>
          );
        })
      )}

      {receiverId && (
        <>
          <div className="fc-hr" />
          {cq.isLoading ? (
            <Skeleton height="58px" borderRadius="11px" />
          ) : channels.length > 0 ? (
            <div>
              <div className="fc-p-t">{t('cafe_wizard.which_channel')}</div>
              <div className="fc-p-s">{t('cafe_wizard.which_channel_hint')}</div>
              {channels.map((c) => (
                <button
                  key={c.id} type="button" className="fc-row"
                  aria-pressed={channelId === c.id}
                  onClick={() => onChannel(c.id)}
                >
                  <span className="ic">📺</span>
                  <span className="b"><span className="t">{c.name}</span></span>
                </button>
              ))}
            </div>
          ) : (
            <div className="fc-inote">{t('cafe_wizard.no_channels_yet_hint')}</div>
          )}
          <div className="fc-inote">{t('cafe_wizard.source_screen_follows_note')}</div>
        </>
      )}
    </div>
  );
}
